import {useMemo} from 'react'
import {Empty, Space, Table, Typography} from 'antd'
import {DeleteOutlined, EditOutlined} from '@ant-design/icons'
import EllipsisTooltip from '../common/EllipsisTooltip'
import RecordsCount from '../common/RecordsCount'
import TableActions from '../common/TableActions'
import {useIntl} from 'react-intl'

const WireTable = ({
  dataSource,
  pagination = false,
  getWiresData = null,
  onEdit = null,
  onDelete = null,
}) => {
  const intl = useIntl()

  const columns = useMemo(
    () => [
      {
        title: intl.formatMessage({ id: 'wire.harness' }),
        dataIndex: 'harness_id',
        sorter: (a, b) => (a.harness_id || '').localeCompare(b.harness_id || ''),
        render: (text) => (
          <EllipsisTooltip className="ellipsis">
            {text || '-'}
          </EllipsisTooltip>
        ),
      },
      {
        title: intl.formatMessage({ id: 'wire.circuit' }),
        dataIndex: 'circuit_name',
        width: 150,
        sorter: (a, b) => (a.circuit_name || '').localeCompare(b.circuit_name || ''),
        render: (text) => (
          <EllipsisTooltip className="ellipsis">
            {text || '-'}
          </EllipsisTooltip>
        ),
      },
      {
        title: intl.formatMessage({ id: 'wire.pin' }),
        dataIndex: 'pin',
        width: 70,
        render: (text, record) => (
          <EllipsisTooltip className="ellipsis">
            {record.pin_from || '-'} / {record.pin_to || '-'}
          </EllipsisTooltip>
        ),
      },
      {
        title: intl.formatMessage({ id: 'wire.connector' }),
        dataIndex: 'connector',
        render: (text, record) => {
          return (
            <EllipsisTooltip className="ellipsis">
                {record.connector_from_name ? (
                  <>
                    <span className="bolder">
                      {intl.formatMessage({ id: 'wire.from' })} :
                    </span>{' '}
                    {record.connector_from_name}
                  </>
                ) : (
                  ''
                )}{' '}
                {record.connector_to_name ? (
                  <>
                    <span className="bolder">
                      {intl.formatMessage({ id: 'wire.to' })} :
                    </span>{' '}
                    {record.connector_to_name}
                  </>
                ) : (
                  ''
                )}
            </EllipsisTooltip>
          )
        },
      },
      {
        title: intl.formatMessage({ id: 'wire.action' }),
        align: 'right',
        width: 136,
        render: (_, record) => (
          <TableActions
            actions={[
              { key: 'edit', label: intl.formatMessage({ id: 'forms.edit' }), icon: <EditOutlined />, onClick: () => onEdit(record) },
              { key: 'delete', label: intl.formatMessage({ id: 'forms.delete' }), icon: <DeleteOutlined />, danger: true, onClick: () => onDelete(record.id) },
            ]}
          />
        ),
      },
    ],
    [dataSource, pagination]
  )

  return (
    <Space
      direction="vertical"
      size="middle"
      style={{ width: '100%', verticalAlign: 'top' }}
    >
      <Table
        rowKey={(record) => `wire-${record.id}`}
        columns={columns}
        dataSource={dataSource}
        showSorterTooltip={false}
        tableLayout='fixed'
        scroll={{
          x: dataSource.length > 0 ? 710 : undefined
        }}
        pagination={
          pagination
            ? {
                current: pagination?.currentPage,
                total: pagination?.totalCount,
                pageSize: 10,
                showSizeChanger: false,
                hideOnSinglePage: true,
                showTotal: (total) => <RecordsCount count={total} />,
                onChange: (page) => {
                  const offset = (page - 1) * 10
                  const url = pagination?.nextPageLink
                    ? new URL(pagination?.nextPageLink)
                    : new URL(pagination?.prevPageLink)
                  url.searchParams.set('offset', offset)
                  getWiresData(url.toString())
                },
              }
            : false
        }
        locale={{
          emptyText: (
            <div style={{ padding: '40px' }}>
              <Empty description={<RecordsCount count={0} />} />
            </div>
          ),
        }}
      />
      {pagination && pagination?.totalPages <= 1 && pagination?.totalCount > 0 && (
        <Typography.Text>
          <RecordsCount count={pagination?.totalCount} />
        </Typography.Text>
      )}
    </Space>
  )
}

export default WireTable
